/** In-memory, single-flight analytics delivery. Retries retain the same payload/ID.
 * No contact data is persisted in browser storage. Closing the page can lose
 * unsent events; this is best-effort telemetry, not a transaction ledger.
 */
export function createEventDelivery<T>(
  send: (payload: T) => Promise<{ ok: boolean }>,
  warn: (reason: "delivery_failed" | "queue_full") => void,
) {
  const queue: T[] = [];
  let sending = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let failures = 0;
  const MAX_PENDING = 500;

  async function drain() {
    if (sending || !queue.length) return;
    sending = true;
    let succeeded = false;
    try {
      const result = await send(queue[0]);
      if (!result?.ok) throw new Error("delivery_failed");
      queue.shift();
      failures = 0;
      succeeded = true;
    } catch {
      failures++;
      // Never log the payload, which may contain private application data.
      if (failures === 1) warn("delivery_failed");
    } finally {
      sending = false;
      if (queue.length) {
        const delay = succeeded ? 0 : Math.min(30_000, 1000 * 2 ** Math.min(failures - 1, 5));
        timer = setTimeout(() => { timer = undefined; void drain(); }, delay);
      }
    }
  }

  return (payload: T) => {
    if (queue.length >= MAX_PENDING) { warn("queue_full"); return; }
    queue.push(payload);
    if (!sending && timer === undefined) void drain();
  };
}
